// components/OrderDetail.tsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Package, CreditCard, User, MapPin } from "lucide-react";
import { Card } from "./ui/card";
import { Badge, type BadgeProps } from "./ui/badge";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { useOrderDetail } from "../hooks/useOrderDetail";
import { statusOptions } from "../lib/orderStatus";
import { toast } from "sonner";
import { formatEuro } from "../lib/utils";
import {
  orderStatusLabel,
  paymentStatusLabel,
  shippingMethodLabel,
  PAYMENT_METHOD_LABELS,
  DISPUTE_STATUS_LABELS,
  cardLabel,
  isDisputeOpen,
  stripePaymentUrl,
} from "../lib/orderLabels";

const OrderDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { order, loading, error, fetchOrderDetail, updateOrderStatus } =
    useOrderDetail();
  const [statusNotes, setStatusNotes] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    if (orderId) {
      fetchOrderDetail(orderId);
    }
  }, [orderId, fetchOrderDetail]);

  useEffect(() => {
    if (opened && order) {
      setNewStatus(order.status);
      setTrackingNumber(order.tracking_number || "");
    }
  }, [opened, order]);

  const requiresTracking = newStatus === "shipped" && !trackingNumber.trim();

  const handleStatusUpdate = async () => {
    if (!orderId || requiresTracking) return;

    const result = await updateOrderStatus(
      orderId,
      newStatus,
      statusNotes.trim() || undefined,
      trackingNumber.trim() || undefined
    );

    if (result.ok) {
      setStatusNotes("");
      setOpened(false);
      if (result.emailError) {
        toast.warning(`Ordine aggiornato, ma l'email al cliente non è partita: ${result.emailError}`);
      } else if (result.emailSent) {
        toast.success("Ordine spedito: email con il tracking inviata al cliente");
      } else {
        toast.success("Stato aggiornato");
      }
    }
  };

  const getStatusVariant = (status: string): BadgeProps["variant"] => {
    const variants: { [key: string]: BadgeProps["variant"] } = {
      pending: "secondary",
      confirmed: "default",
      processing: "default",
      shipped: "default",
      delivered: "eco",
      cancelled: "destructive",
      refunded: "secondary",
    };
    return variants[status] || "secondary";
  };

  const getPaymentStatusVariant = (status: string): BadgeProps["variant"] => {
    const variants: { [key: string]: BadgeProps["variant"] } = {
      pending: "secondary",
      paid: "eco",
      failed: "destructive",
      refunded: "secondary",
      cancelled: "destructive",
    };
    return variants[status] || "secondary";
  };

  if (loading) {
    return (
      <div className="container py-10">
        <div className="flex justify-center items-center py-20">
          <p>Caricamento dettaglio ordine...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="container py-10">
        <div className="text-center py-20">
          <p className="text-danger mb-4">
            {error || "Ordine non trovato"}
          </p>
          <Button onClick={() => navigate("/admin/dashboard?view=ordini")}>
            Torna alla lista ordini
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-10">
      {/* Header */}
      <div className="flex flex-col gap-4 mb-8 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col items-start gap-4 md:flex-row md:items-center">
          <Button variant="outline" onClick={() => navigate("/admin/dashboard?view=ordini")}>
            <ArrowLeft size={16} />
            Torna agli ordini
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Ordine #{order.id.slice(-8).toUpperCase()}
            </h1>
            <p className="text-muted-foreground">
              Creato il {new Date(order.created_at).toLocaleDateString("it-IT")}
            </p>
          </div>
        </div>

        <Button className="w-full md:w-auto" onClick={() => setOpened(true)}>Aggiorna Stato</Button>
      </div>

      {order.dispute_status && (
        <div
          className={`mb-6 rounded-lg border p-4 ${
            isDisputeOpen(order.dispute_status)
              ? "border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/30 text-red-900 dark:text-red-200"
              : "border-border bg-muted text-foreground"
          }`}
        >
          <p className="font-semibold">
            {DISPUTE_STATUS_LABELS[order.dispute_status] ?? `Contestazione: ${order.dispute_status}`}
          </p>
          {isDisputeOpen(order.dispute_status) && (
            <p className="mt-1 text-sm">
              Il cliente ha contestato il pagamento con la sua banca. Non spedire finché non è risolta: le prove
              (ricevuta, tracking, comunicazioni) si inviano dalla dashboard Stripe.
              {order.payment_intent_id && (
                <>
                  {" "}
                  <a
                    href={stripePaymentUrl(order.payment_intent_id, order.stripe_livemode)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold underline"
                  >
                    Apri su Stripe ↗
                  </a>
                </>
              )}
            </p>
          )}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Colonna Sinistra */}
        <div className="min-w-0 lg:w-2/3 space-y-6">
          {/* Prodotti dell'ordine */}
          <Card className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Prodotti Ordinati</h2>
              <Badge variant="secondary">{order.items_count} articoli</Badge>
            </div>

            {/* Da telefono le quattro colonne non ci stanno: una scheda per riga. */}
            <ul className="divide-y divide-border md:hidden">
              {order.items.map((item) => (
                <li key={item.id} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                  <img
                    src={item.product_image}
                    alt=""
                    className="h-12 w-12 flex-none rounded object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{item.product_name}</p>
                    <p className="text-sm text-muted-foreground">
                      {item.variant_label ? `Formato ${item.variant_label} · ` : ''}SKU: {item.product_id.slice(-8)}
                    </p>
                    <p className="mt-1 flex items-baseline justify-between gap-3 text-sm">
                      <span className="text-muted-foreground">{item.quantity} × {formatEuro(item.price)}</span>
                      <span className="font-semibold">{formatEuro(item.subtotal)}</span>
                    </p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b">
                    <th className="py-2 font-medium text-muted-foreground">Prodotto</th>
                    <th className="py-2 font-medium text-muted-foreground">Prezzo</th>
                    <th className="py-2 font-medium text-muted-foreground">Quantità</th>
                    <th className="py-2 font-medium text-muted-foreground">Totale</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item) => (
                    <tr key={item.id} className="border-b last:border-0">
                      <td className="py-3">
                        <div className="flex items-center space-x-3">
                          <img
                            src={item.product_image}
                            alt={item.product_name}
                            className="w-12 h-12 object-cover rounded"
                          />
                          <div>
                            <p className="font-medium">{item.product_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {item.variant_label ? `Formato ${item.variant_label} · ` : ''}SKU: {item.product_id.slice(-8)}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3">{formatEuro(item.price)}</td>
                      <td className="py-3">{item.quantity}</td>
                      <td className="py-3 font-semibold">{formatEuro(item.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Cronologia Stato */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-6">Cronologia Stato</h2>

            <ol className="relative border-l border-dashed border-border ml-3 space-y-6">
              <li className="ml-6">
                <span className="absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  <Package size={14} />
                </span>
                <p className="font-medium text-sm">Creazione ordine</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(order.created_at).toLocaleDateString("it-IT")}
                </p>
              </li>
              {order.status_history.map((history) => (
                <li key={history.id} className="ml-6">
                  <span className="absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
                    <Package size={14} />
                  </span>
                  <p className="font-medium text-sm">
                    {orderStatusLabel(history.status)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {history.created_at &&
                      new Date(history.created_at).toLocaleString("it-IT")}
                  </p>
                  {history.notes && (
                    <p className="text-sm mt-1 text-muted-foreground">{history.notes}</p>
                  )}
                </li>
              ))}
            </ol>
          </Card>
        </div>

        {/* Colonna Destra - Sidebar */}
        <div className="min-w-0 lg:w-1/3 space-y-6">
          {order.notes && (
            <Card className="border-l-4 border-l-primary bg-amber-50 dark:bg-amber-900/30 p-6">
              <h2 className="text-lg font-semibold mb-2">Note del cliente</h2>
              <p className="text-sm whitespace-pre-line">{order.notes}</p>
            </Card>
          )}

          {/* Riepilogo Ordine */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Riepilogo Ordine</h2>

            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Stato Ordine:</span>
                <Badge variant={getStatusVariant(order.status)}>{orderStatusLabel(order.status)}</Badge>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Pagamento:</span>
                <Badge variant={getPaymentStatusVariant(order.payment_status)}>
                  {paymentStatusLabel(order.payment_status)}
                </Badge>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Subtotale:</span>
                <span>{formatEuro(order.total_price)}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Spedizione:</span>
                <span>{formatEuro(order.shipping_price)}</span>
              </div>

              <div className="flex justify-between items-center border-t pt-3">
                <span className="font-semibold">Totale:</span>
                <span className="font-semibold text-lg">
                  {formatEuro((order.total_price + order.shipping_price))}
                </span>
              </div>
            </div>
          </Card>

          {/* Informazioni Cliente */}
          <Card className="p-6">
            <div className="flex items-center space-x-2 mb-4">
              <User size={20} />
              <h2 className="text-xl font-semibold">Cliente</h2>
            </div>

            <div className="space-y-2">
              <div>
                <p className="font-medium">{order.customer_name}</p>
                <a href={`mailto:${order.customer_email}`} className="block break-words text-sm text-muted-foreground hover:text-primary">{order.customer_email}</a>
                {order.customer_phone && (
                  <a href={`tel:${order.customer_phone}`} className="block text-sm text-muted-foreground hover:text-primary">{order.customer_phone}</a>
                )}
              </div>

              {order.user_id && (
                <Badge variant="secondary" className="mt-2">
                  Utente Registrato
                </Badge>
              )}
            </div>
          </Card>

          {/* Indirizzo di Spedizione */}
          <Card className="p-6">
            <div className="flex items-center space-x-2 mb-4">
              <MapPin size={20} />
              <h2 className="text-xl font-semibold">Spedizione</h2>
            </div>

            {order.shipping_address && (
              <div className="space-y-2">
                <Badge variant="secondary">{shippingMethodLabel(order.shipping_method)}</Badge>
                {order.shipping_method === "pickup" ? (
                  <p className="text-sm text-muted-foreground">
                    Ritiro in apiario: contatta il cliente per concordare giorno e orario.
                  </p>
                ) : (
                  <>
                    <p className="font-medium">{order.customer_name}</p>
                    <p className="text-sm">
                      {order.shipping_address.street}
                      {order.shipping_address.apartment && `, ${order.shipping_address.apartment}`}
                    </p>
                    <p className="text-sm">
                      {order.shipping_address.postalCode} {order.shipping_address.city}
                      {order.shipping_address.province && ` (${order.shipping_address.province})`}
                    </p>
                    <p className="text-sm">{order.shipping_address.country}</p>
                  </>
                )}
                {order.customer_phone && (
                  <p className="text-sm text-muted-foreground">Tel: {order.customer_phone}</p>
                )}

                {order.tracking_number && (
                  <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-900/30 rounded border border-blue-200 dark:border-blue-800">
                    <p className="text-sm font-medium">
                      Tracking: {order.tracking_number}
                    </p>
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Pagamento (dati salvati da stripe-webhook) */}
          <Card className="p-6">
            <div className="flex items-center space-x-2 mb-4">
              <CreditCard size={20} />
              <h2 className="text-xl font-semibold">Pagamento</h2>
            </div>

            {order.payment_status === "paid" || order.payment_status === "refunded" || order.amount_paid != null ? (
              <div className="space-y-2">
                <div className="flex justify-between items-center gap-3">
                  <span className="text-muted-foreground">Metodo:</span>
                  <span className="text-right">
                    {cardLabel(order.card_brand, order.card_last4) ??
                      PAYMENT_METHOD_LABELS[order.payment_method] ??
                      order.payment_method}
                  </span>
                </div>

                {order.amount_paid != null && (
                  <div className="flex justify-between items-center gap-3">
                    <span className="text-muted-foreground">Incassato:</span>
                    <span className="font-medium">{formatEuro(order.amount_paid)}</span>
                  </div>
                )}

                {order.amount_paid != null &&
                  Math.abs(order.amount_paid - (order.total_price + order.shipping_price)) >= 0.01 && (
                    <p className="rounded bg-amber-50 dark:bg-amber-900/30 p-2 text-sm text-amber-900 dark:text-amber-200">
                      L'importo incassato non coincide con il totale dell'ordine (
                      {formatEuro(order.total_price + order.shipping_price)}): controlla su Stripe prima di spedire.
                    </p>
                  )}

                {order.refunded_amount > 0 && (
                  <div className="flex justify-between items-center gap-3">
                    <span className="text-muted-foreground">Rimborsato:</span>
                    <span className="font-medium text-danger">− {formatEuro(order.refunded_amount)}</span>
                  </div>
                )}

                {order.stripe_livemode === false && (
                  <Badge variant="secondary">Pagamento di prova (Stripe in modalità test)</Badge>
                )}

                {order.payment_intent_id && (
                  <a
                    href={stripePaymentUrl(order.payment_intent_id, order.stripe_livemode)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-block text-sm font-semibold text-primary hover:underline"
                  >
                    Apri su Stripe ↗
                  </a>
                )}
                {!order.payment_intent_id && (
                  <p className="text-sm text-muted-foreground">
                    Nessun pagamento Stripe collegato (ordine segnato come pagato a mano).
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {order.payment_status === "failed"
                  ? "Pagamento non riuscito."
                  : "Pagamento non ancora ricevuto."}
              </p>
            )}
          </Card>
        </div>
      </div>

      {/* Modal Aggiornamento Stato */}
      <Dialog open={opened} onOpenChange={setOpened}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aggiorna Stato Ordine</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <label className="text-sm font-medium">Nuovo Stato</label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions(order.status).map((st) => (
                    <SelectItem key={st} value={st}>{orderStatusLabel(st)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {newStatus === "shipped" && (
              <div className="space-y-1">
                <label className="text-sm font-medium">Numero di Tracking *</label>
                <Input
                  placeholder="Es. AB123456789IT"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Al salvataggio, se è la prima volta che l'ordine passa a "Spedito", il cliente riceve una email con questo tracking.
                </p>
              </div>
            )}

            <div className="space-y-1">
              <label className="text-sm font-medium">Note (opzionale)</label>
              <Textarea
                placeholder="Aggiungi note per questo cambio di stato..."
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpened(false)}>
                Annulla
              </Button>
              <Button
                onClick={handleStatusUpdate}
                disabled={requiresTracking}
              >
                Aggiorna Stato
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderDetail;
